// Resolves the whitelabel institution hub palette from a partner's
// `landing_page_config`, guarding every derived color with a WCAG contrast
// check so that light brand colors stay legible.
//
// All policy lives here (not in the components) so it can be unit-tested
// without rendering.

import { contrastRatio } from 'helpers'
import { ncfddLogoOnDark, ncfddLogoOnWhite } from 'images'

// WCAG AA for normal text. A color is "usable" as ink/fill on a background
// when it clears this ratio against that background.
const AA = 4.5
const WHITE = '#ffffff'

// Matches the hardcoded default heading/nav color throughout sections/style.scss.
const NCFDD_NAVY = '#1a2e5c'
// Neutral used for non-branded text on this page (e.g. the nav label / institution
// name when the partner has no readable colour to use). Matches $blackHeading.
const NEUTRAL_DARK = '#000000'

export interface LandingPageConfig {
  primary_color?: string
  text_color?: string
  button_color?: string
}

export interface Branding {
  navBackground: string | null
  navIsLight: boolean
  ncfddLogo: string
  navLabelColor: string | null
  buttonBackground: string | null
  headingColor: string | null
  linkColor: string | null
}

const usableOn = (color: string | undefined, background: string): boolean => {
  if (!color) return false
  const ratio = contrastRatio(color, background)
  return ratio !== null && ratio >= AA
}

const resolveBranding = (config: LandingPageConfig = {}): Branding => {
  const primary = config.primary_color || undefined
  const text = config.text_color || undefined
  const buttonColor = config.button_color || undefined

  // Only treat `primary` as a real background if it parses; otherwise the nav
  // keeps its stylesheet default and nothing else should react to it.
  const primaryParses = contrastRatio(primary || '', WHITE) !== null
  const navBackground = primaryParses ? primary : null
  const navIsLight = primaryParses ? !usableOn(WHITE, primary as string) : false
  const effectiveNavBg = navBackground || NCFDD_NAVY

  const navLabelColor = navIsLight
    ? (usableOn(text, effectiveNavBg) ? (text as string) : NEUTRAL_DARK)
    : null

  let buttonBackground: string | null
  if (usableOn(buttonColor, WHITE)) {
    buttonBackground = buttonColor as string
  } else if (usableOn(primary, WHITE)) {
    buttonBackground = primary as string
  } else if (usableOn(text, WHITE)) {
    buttonBackground = text as string
  } else {
    buttonBackground = null
  }

  const preferredHeading = text || primary
  const headingColor = usableOn(preferredHeading, WHITE)
    ? (preferredHeading as string)
    : NCFDD_NAVY

  const linkColor = usableOn(text, WHITE) ? (text as string) : null

  return {
    navBackground,
    navIsLight,
    ncfddLogo: navIsLight ? ncfddLogoOnWhite : ncfddLogoOnDark,
    navLabelColor,
    buttonBackground,
    headingColor,
    linkColor
  }
}

export default resolveBranding
